import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    CheckCircleIcon,
    UserIcon,
    EnvelopeIcon,
    KeyIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { getCandidateInfo, submitEmployeeCredentials } from "../../api/offeredcandidate.api";
import useApiToken from "../../hooks/useApiToken";

type Buddy = {
    employeeName: string;
    employeeEmail: string;
};

type CandidateInfo = {
    offerId: string,
    candidateId: string;
    candidateName: string;
    candidateEmail: string;
    role: string;
    startDate: string;
};

export const SubmitCredentialsPage = () => {
    const { offerId } = useParams<{ offerId: string }>();
    const navigate = useNavigate();
    const token = useApiToken();

    const [candidateInfo, setCandidateInfo] = useState<CandidateInfo | null>(null);
    const [employeeEmail, setEmployeeEmail] = useState("");
    const [temporaryPassword, setTemporaryPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchCandidateInfo();
        if (offerId) {
        }
    }, [offerId]);

    const fetchCandidateInfo = async () => {
        try {
            setIsLoading(true);
            const data = await getCandidateInfo(offerId!, token!);
            setCandidateInfo(data);
            // Pre-fill employee email
        } catch (err: any) {
            setError(err.message || "Failed to load candidate information");
        } finally {
            setIsLoading(false);
        }
    };

    function formatDate(dateInput: string | Date): string {
        let date: Date;

        if (typeof dateInput === "string") {
            date = new Date(dateInput);
        } else {
            date = dateInput;
        }

        if (isNaN(date.getTime())) return "";

        const day = date.getDate();
        const month = date.toLocaleString("default", { month: "long" });
        const year = date.getFullYear();

        return `${day} ${month} ${year}`;
    }
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!employeeEmail.trim()) {
            setError("Employee email is required");
            return;
        }

        if (!temporaryPassword.trim()) {
            setError("Temporary password is required");
            return;
        }

        try {
            setIsSubmitting(true);

            await submitEmployeeCredentials({
                offerId: offerId!,
                employeeEmail: employeeEmail.trim(),
                temporaryPassword: temporaryPassword.trim(),
                token: token!,
            });

            setSuccess(true);
        } catch (err: any) {
            setError(err.message || "Failed to submit credentials");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-darkbg flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">Loading candidate information...</p>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-darkbg flex items-center justify-center p-4">
                <div className="bg-white dark:bg-darkbglight rounded-xl shadow-lg p-8 max-w-md w-full text-center border border-transparent dark:border-gray-700">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircleIcon className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-200 mb-2">Credentials Sent!</h2>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                        The credentials have been successfully sent to all assigned buddies.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-darkbg py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="bg-white dark:bg-darkbglight rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-200 mb-2">
                        Submit Employee Credentials
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Enter the credentials to be shared with the assigned buddies
                    </p>
                    {/* Candidate Info */}
                    {candidateInfo && (
                        <div className="bg-gray-50 dark:bg-darkbg rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-200 mb-6 flex items-center gap-2">
                                <UserIcon className="w-5 h-5" />
                                Employee Information
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Name</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-200">{candidateInfo.candidateName}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-200">{candidateInfo.candidateEmail}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Role</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-200">{candidateInfo.role}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Start Date</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-200">{formatDate(candidateInfo.startDate)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>



                {/* Credentials Form */}
                <div className="bg-white dark:bg-darkbglight rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Employee Email */}
                        <div>

                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                <EnvelopeIcon className="w-4 h-4 inline mr-2" />
                                Employee Email
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                                type="email"
                                value={employeeEmail}
                                onChange={(e) => setEmployeeEmail(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbg text-gray-900 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="employee@entnt.in"
                                required
                                disabled={isSubmitting}
                            />

                        </div >

                        {/* Temporary Password */}
                        < div >
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                <KeyIcon className="w-4 h-4 inline mr-2" />
                                Temporary Password
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                            <div className="flex gap-3">
                                <input
                                    type="text"
                                    value={temporaryPassword}
                                    onChange={(e) => setTemporaryPassword(e.target.value)}
                                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbg text-gray-900 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter temporary password"
                                    required
                                    disabled={isSubmitting}
                                />

                            </div >
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                Employee must change password on first login
                            </p>
                        </div >


                        {/* Error Message */}
                        {
                            error && (
                                <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                                    <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
                                </div>
                            )
                        }

                        {/* Submit Button */}
                        <div className="flex gap-4 pt-4">
                            <button
                                type="button"
                                onClick={() => navigate("/admin/dashboard")}
                                disabled={isSubmitting}
                                className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        <span>Sending...</span>
                                    </>
                                ) : (
                                    <>
                                        <EnvelopeIcon className="w-5 h-5" />
                                        <span>Send to Buddies</span>
                                    </>
                                )}
                            </button>
                        </div>


                    </form>
                </div>




            </div >
        </div >
    );
};
